import type { CustomizableColumn } from "Components/Common/ColumnCustomizeDialog";

/** Stable key for /v2/table-preferences `page_or_component` (max 25 chars in DB). */
export const PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT = "ProfitabilityBreakdown";

/** @deprecated Use PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT */
export const PROFITABILITY_ANALYSIS_PAGE_OR_COMPONENT =
  PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT;

export type ColumnDefLike = {
  field: string;
  headerName: string;
  pinned?: "left" | "right" | boolean | null;
  /** Optional explicit lock flag on the column def. */
  locked?: boolean;
  [key: string]: unknown;
};

export type ToColumnPrefsOptions = {
  /** Default visibility for non-locked columns. Defaults to false. */
  defaultVisible?: boolean;
  /**
   * Decide which columns are locked in Customise (always visible, no drag/hide).
   * Default: lock columns with `locked: true`.
   */
  isLocked?: (def: ColumnDefLike) => boolean;
};

const defaultIsLocked = (def: ColumnDefLike) =>
  def.locked === true;

/** Build serializable prefs from full grid column defs (renderers are NOT included). */
export const toColumnPrefs = (
  defs: ColumnDefLike[],
  options?: ToColumnPrefsOptions,
): CustomizableColumn[] => {
  const defaultVisible = options?.defaultVisible ?? false;
  const isLocked = options?.isLocked ?? defaultIsLocked;

  return defs.map((column, index) => {
    const locked = isLocked(column);
    return {
      id: column.field,
      field: column.field,
      headerName: column.headerName,
      isVisible: locked || defaultVisible,
      locked,
      order: index + 1,
    };
  });
};

/**
 * Re-apply lock rules from current defs onto saved prefs
 * (so code changes to pinned/locked take effect after reload).
 */
export const syncLockedPrefs = (
  prefs: CustomizableColumn[],
  defs: ColumnDefLike[],
  options?: Pick<ToColumnPrefsOptions, "isLocked">,
): CustomizableColumn[] => {
  const isLocked = options?.isLocked ?? defaultIsLocked;
  const defByField = new Map(defs.map((def) => [def.field, def]));

  return prefs.map((pref) => {
    const def = defByField.get(pref.field);
    const locked = def ? isLocked(def) : !!pref.locked;
    return {
      ...pref,
      locked,
      // Undefined / missing means hidden for optional columns (unchecked by default).
      isVisible: locked ? true : !!pref.isVisible,
    };
  });
};

/**
 * Merge saved prefs onto full column defs (keeps cellRenderers / pinned / filters from defs).
 * Order + visibility come from Redux prefs. Locked columns are always treated as visible.
 */
export const applyColumnPrefs = <T extends ColumnDefLike>(
  defs: T[],
  prefs: CustomizableColumn[],
): T[] => {
  if (!prefs?.length) {
    return defs;
  }

  const byField = new Map(defs.map((column) => [column.field, column]));
  const sortedPrefs = prefs
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const mapPrefToDef = (pref: CustomizableColumn): T | null => {
    const base = byField.get(pref.field);
    if (!base) return null;
    return {
      ...base,
      headerName: pref.headerName || base.headerName,
    };
  };

  const visibleApplied = sortedPrefs
    .filter((pref) => {
      if (!byField.has(pref.field)) return false;
      if (pref.locked) return true;
      if (pref.hide === true || pref.isVisible === false) return false;
      return pref.isVisible === true;
    })
    .map(mapPrefToDef)
    .filter((column): column is T => column != null);

  const optionalVisible = visibleApplied.some((column) => !column.locked);

  // Until the user checks at least one optional column, show the full header
  // set (in saved order) so the grid stays usable with default-unchecked prefs.
  if (!visibleApplied.length || !optionalVisible) {
    const orderedAll = sortedPrefs
      .map(mapPrefToDef)
      .filter((column): column is T => column != null);
    const seen = new Set(orderedAll.map((column) => column.field));
    defs.forEach((column) => {
      if (!seen.has(column.field)) {
        orderedAll.push(column);
      }
    });
    return orderedAll.length ? orderedAll : defs;
  }

  return visibleApplied;
};

/**
 * Ensure locked columns stay visible. Does NOT force optional columns on —
 * unchecked-by-default / user hide selections must be preserved.
 */
const resolveColumnField = (column: CustomizableColumn): string =>
  column.field || (column as { key?: string }).key || column.id || "";

/** Keep one entry per field; explicit visible choice wins over an older hidden duplicate. */
export const dedupeColumnPrefsByField = (
  prefs: CustomizableColumn[],
): CustomizableColumn[] => {
  if (!prefs?.length) return [];

  const groups = new Map<string, CustomizableColumn[]>();
  prefs.forEach((column) => {
    const field = resolveColumnField(column);
    if (!field) return;
    const existing = groups.get(field) ?? [];
    existing.push(column);
    groups.set(field, existing);
  });

  const pickWinner = (duplicates: CustomizableColumn[]): CustomizableColumn => {
    const normalized: CustomizableColumn[] = duplicates.map((column) => {
      const field = resolveColumnField(column);
      return { ...column, id: column.id || field, field };
    });

    const explicitlyVisible = normalized.filter(
      (column) =>
        !column.locked &&
        column.isVisible === true &&
        (column as { hide?: boolean }).hide !== true,
    );
    if (explicitlyVisible.length > 0) {
      return explicitlyVisible.reduce((best, column) =>
        (column.order ?? 0) > (best.order ?? 0) ? column : best,
      );
    }

    return normalized
      .slice()
      .sort(
        (a, b) =>
          (a.order ?? Number.MAX_SAFE_INTEGER) -
          (b.order ?? Number.MAX_SAFE_INTEGER),
      )[0];
  };

  const winners = Array.from(groups.values())
    .map(pickWinner)
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER),
    );

  return winners.map((column, index) => ({
    ...column,
    order: index + 1,
  }));
};

export const normalizePrefsVisibility = (
  prefs: CustomizableColumn[],
): CustomizableColumn[] => {
  if (!prefs?.length) return prefs;
  return prefs.map((column, index) => {
    const locked = !!column.locked;
    const isVisible = locked ? true : !!column.isVisible;
    return {
      ...column,
      locked,
      isVisible,
      hide: locked ? false : !isVisible,
      order: column.order ?? index + 1,
    };
  });
};

/** True when at least one non-locked column is visible (a usable customise selection). */
export const hasVisibleOptionalColumns = (prefs: CustomizableColumn[]): boolean =>
  prefs.some(
    (column) =>
      !column.locked && column.isVisible === true && column.hide !== true,
  );

/**
 * Merge API/server column prefs with current hardcoded defs (network-style).
 * Prefs win for order/visibility/headerName; defs supply live renderers + lock rules.
 */
export const mergeApiColumnsWithDefs = (
  apiColumns: CustomizableColumn[],
  columnDefs: ColumnDefLike[],
  options?: Pick<ToColumnPrefsOptions, "isLocked">,
): CustomizableColumn[] => {
  const isLocked = options?.isLocked ?? defaultIsLocked;
  const defByField = new Map(columnDefs.map((def) => [def.field, def]));
  const normalizedApiColumns = dedupeColumnPrefsByField(
    apiColumns.map((col) => ({
      ...col,
      field: col.field || (col as { key?: string }).key || col.id,
    })),
  );
  const apiByField = new Map(
    normalizedApiColumns.map((col) => [col.field || (col as any).key, col]),
  );

  const merged = normalizedApiColumns.map((apiCol, index) => {
    const field = apiCol.field || (apiCol as any).key;
    const def = defByField.get(field);
    const locked = def ? isLocked(def) : !!apiCol.locked;
    // Prefer explicit isVisible; fall back to hide flag; default unchecked.
    const apiVisible =
      apiCol.isVisible === true ||
      (apiCol.isVisible !== false && apiCol.hide === false);
    const isVisible = locked ? true : apiVisible;
    return {
      id: field,
      field,
      headerName: apiCol.headerName || def?.headerName || field,
      isVisible,
      locked,
      order: apiCol.order ?? index + 1,
      hide: locked ? false : !isVisible,
    } as CustomizableColumn;
  });

  columnDefs.forEach((def, index) => {
    if (!apiByField.has(def.field)) {
      const locked = isLocked(def);
      merged.push({
        id: def.field,
        field: def.field,
        headerName: def.headerName,
        // New columns not in saved prefs default to unchecked (locked stay on).
        isVisible: locked,
        locked,
        order: merged.length + index + 1,
      });
    }
  });

  return syncLockedPrefs(merged, columnDefs, options);
};
